import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Carousel, Button, Alert, Spinner, Form, Card } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { useCart } from '../contexts/CartContext';
import { useBranding } from '../contexts/BrandingContext';
import ProductMediaModal from '../components/ProductMediaModal';

const ProductDetails = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [mediaModalState, setMediaModalState] = useState({
    show: false,
    items: [],
    title: '',
    index: 0
  });
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { refreshCart } = useCart();
  const { branding } = useBranding();

  const getFallbackImageUrl = () => branding?.logo_url || '/img/Tonisco-logo-wide-website-2048x649.png';

  const handleImageError = (event) => {
    const fallbackSrc = getFallbackImageUrl();
    if (event?.currentTarget && event.currentTarget.src !== fallbackSrc) {
      event.currentTarget.onerror = null;
      event.currentTarget.src = fallbackSrc;
    }
  };

  useEffect(() => {
    if (user) {
      fetchProduct();
    } else {
      navigate('/login');
    }
  }, [id, user]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/products/${id}`);
      setProduct(response.data);
      setError(null);
    } catch (err) {
      setError(t('common.error'));
      console.error('Failed to fetch product:', err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    try {
      await api.post('/api/cart/add', { productId: id, quantity });
      notify({ variant: 'success', message: t('common.success') });
      refreshCart();
      setQuantity(1); // Reset quantity after adding to cart
    } catch (err) {
      notify({ variant: 'danger', message: t('common.error') });
      console.error('Failed to add to cart:', err);
    }
  };

  const getMediaUrl = (media) => {
    if (!media) return '';
    const rawUrl = typeof media === 'string'
      ? media
      : (media.imageUrl || media.url || media.path || '');
    if (!rawUrl) return '';
    if (rawUrl.startsWith('/uploads') && api?.defaults?.baseURL) {
      return `${api.defaults.baseURL}${rawUrl}`;
    }
    if (rawUrl.startsWith('uploads/') && api?.defaults?.baseURL) {
      return `${api.defaults.baseURL}/${rawUrl}`;
    }
    if (rawUrl.startsWith('http://') && window.location.protocol === 'https:') {
      return rawUrl.replace('http://', 'https://');
    }
    return rawUrl;
  };

  const resolvedManualUrl = (product?.product_url || product?.manual_url || '').trim();

  const isVideo = (media) => {
    const url = getMediaUrl(media);
    return /\.(mp4|webm|ogg)$/i.test(url);
  };

  const openMediaModal = (startIndex) => {
    const items = Array.isArray(product?.images) ? product.images : [];
    if (items.length === 0) {
      return;
    }

    setMediaModalState({
      show: true,
      items,
      title: product?.name || t('products.title'),
      index: Number.isInteger(startIndex) ? startIndex : 0
    });
  };

  const closeMediaModal = () => {
    setMediaModalState((previous) => ({
      ...previous,
      show: false
    }));
  };

  if (loading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error || t('products.notFound')}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row>
        <Col md={6}>
          {product.images && product.images.length > 0 ? (
            <Carousel controls={product.images.length > 1} indicators={product.images.length > 1} interval={null}>
              {product.images.map((media, index) => (
                <Carousel.Item key={index}>
                  {isVideo(media) ? (
                    <video className="d-block w-100" controls preload="metadata" style={{ maxHeight: '500px' }}>
                      <source src={getMediaUrl(media)} />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img
                      className="d-block w-100"
                      src={getMediaUrl(media)}
                      alt={`${product.name} - ${index + 1}`}
                      style={{ maxHeight: '500px', objectFit: 'contain' }}
                      onError={handleImageError}
                      role="button"
                      tabIndex={0}
                      onClick={() => openMediaModal(index)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openMediaModal(index);
                        }
                      }}
                    />
                  )}
                </Carousel.Item>
              ))}
            </Carousel>
          ) : (
            <img
              className="d-block w-100"
              src={getFallbackImageUrl()}
              alt={t('products.noImages')}
              onError={handleImageError}
              style={{ maxHeight: '500px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
            />
          )}
        </Col>
        
        <Col md={6}>
          <h1>{product.name}</h1>
          <p className="lead">{product.description}</p>
          {resolvedManualUrl && (
            <p className="mb-3">
              <a href={resolvedManualUrl} target="_blank" rel="noopener noreferrer">
                {t('products.openManual')}
              </a>
            </p>
          )}
          
          <div className="mb-3">
            <strong>{t('products.stock')}:</strong>{' '}
            {product.stock > 0 ? (
              <span className="stock-indicator in-stock" title={t('products.inStock')}>
                ✔ {t('products.inStock')}
              </span>
            ) : (
              <span className="stock-indicator out-of-stock" title={t('products.outOfStock')}>
                ✖ {t('products.outOfStock')}
              </span>
            )}
          </div>
          
          {product.stock > 0 && (
            <>
              <Form.Group className="mb-3">
                <Form.Label><strong>{t('cart.quantity')}:</strong></Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max={product.stock}
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  style={{ width: '100px' }}
                />
              </Form.Group>
              
              <Button 
                variant="primary" 
                size="lg" 
                onClick={addToCart}
                disabled={quantity < 1 || quantity > product.stock}
              >
                {t('products.addToCart')}
              </Button>
            </>
          )}
          
          {product.stock === 0 && (
            <Alert variant="warning">{t('products.outOfStockAlert')}</Alert>
          )}
        </Col>
      </Row>

      {product.linkedProducts && product.linkedProducts.length > 0 && (
        <div className="linked-products-section mt-5">
          <h3 className="mb-3">{t('products.linkedTitle')}</h3>
          <Row xs={1} md={2} lg={3} className="g-4">
            {product.linkedProducts.map((linked) => (
              <Col key={linked.id}>
                <Card className="product-card h-100">
                  {linked.images && linked.images.length > 0 ? (
                    <Card.Img
                      variant="top"
                      src={getMediaUrl(linked.images[0])}
                      className="product-image"
                      alt={linked.name}
                      onError={handleImageError}
                    />
                  ) : (
                    <Card.Img
                      variant="top"
                      src={getFallbackImageUrl()}
                      className="product-image"
                      alt={t('products.noMedia')}
                      onError={handleImageError}
                    />
                  )}
                  <Card.Body className="d-flex flex-column">
                    <Card.Title>{linked.name}</Card.Title>
                    <Card.Text className="linked-product-description flex-grow-1">{linked.description}</Card.Text>
                    <div className="mb-3">
                      <strong>{t('products.stock')}:</strong>{' '}
                      {linked.stock > 0 ? (
                        <span className="stock-indicator in-stock" title={t('products.inStock')}>
                          ✔ {t('products.inStock')}
                        </span>
                      ) : (
                        <span className="stock-indicator out-of-stock" title={t('products.outOfStock')}>
                          ✖ {t('products.outOfStock')}
                        </span>
                      )}
                    </div>
                    <Button as={Link} to={`/products/${linked.id}`} variant="outline-primary">
                      {t('products.viewDetails')}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <ProductMediaModal
        show={mediaModalState.show}
        onHide={closeMediaModal}
        mediaItems={mediaModalState.items}
        initialIndex={mediaModalState.index}
        title={mediaModalState.title}
        resolveMediaUrl={getMediaUrl}
        isVideo={isVideo}
      />
    </Container>
  );
};

export default ProductDetails;
